/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { CreditCardIcon, DocumentReportIcon, DocumentTextIcon, ServerIcon } from '@heroicons/react/solid'
import images from 'react-payment-inputs/images'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Bubble, Button, FrameInset, Tabs, Theme } from '@nexigendigital/nxui'
import { useAccountCreditQuery, useAccountPaymentMethodsQuery, useInvoicesQuery } from 'api/billing'
import { Weight } from 'models/enums'
import { Invoice, PaymentMethodData } from 'models/billing'
import { setInvoice } from 'store/slices/billingSlice'
import { aud } from 'helpers/utils'

import Invoices from 'containers/billing/invoices'
import Transactions from 'containers/billing/transactions'
import PaymentMethods from 'containers/billing/paymentMethods'

/**********************************************************************************************************
 *   STYLE
 **********************************************************************************************************/
import styled from 'styled-components'

const Overview = styled.section`
    width: 100%;
`

const TabsWrapper = styled(Tabs)`
    margin-bottom: 40px;

    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-xl']))} {
        margin-top: 16px;
    }

    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-lg']))} {
        margin-bottom: 80px;
    }
`

const AccountPaymentWrapper = styled.div`
    margin-top: 1rem;
    font-size: .875rem;
    background-color: ${props => props.theme['white--100']};
    border-radius: 16px;
    border: 1px solid ${props => props.theme['gray--100']};
    box-shadow: 0 1px 3px ${props => Theme.Functions.hexToRGBA(props.theme['black--200'], '0.1')},
    0 1px 2px ${props => Theme.Functions.hexToRGBA(props.theme['black--200'], '0.06')};
    ${Theme.Functions.setFont('Inter', Weight.Medium)};
`

const Payment = {
    Container: styled.div`
        width: 100%;
        padding: 18px 24px;
        display: flex;
        justify-content: space-between;

        &:first-child {
            border-bottom: 1px solid ${props => props.theme['gray--100']};
        }
    `,
    Title: styled.div`
        color: ${props => props.theme['text--100']}
    `
}

const Method = {
    Default: {
        Container: styled.div`
            display: flex;
            flex-direction: row;
            align-items: center;
        `,
        Type: styled.div`
            svg {
                height: 16px;
                width: 40px;
            }
        `,
        Identifier: styled.div`
            color: ${props => props.theme['text--100']}
        `
    }
}

const InvoicePaymentWrapper = styled.div`
    width: 100%;
    margin-top: 1rem;
    display: flex;
    justify-content: space-between;
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-direction: column;
        gap: 10px;
    }

    > button {
        width: 47%;
        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
            width: 100%;
            margin: 0;
        }
    }
`

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Billing() {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()

    /*   STATE
     **********************************************************************************************************/
    const [outstandingInvoices, setOutstandingInvoices] = useState<Invoice[] | undefined>(undefined)
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<PaymentMethodData | undefined>(undefined)

    /*   QUERIES & MUTATIONS
     **********************************************************************************************************/
    const { data: invoices = [], isLoading: invoicesLoading, isSuccess: invoicesSuccess } = useInvoicesQuery(1)
    const { data: paymentMethods = [], isSuccess: paymentMethodsSuccess } = useAccountPaymentMethodsQuery()
    const { data } = useAccountCreditQuery()

    /*   EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if (invoicesSuccess && invoices?.data.length > 0) {
            const unpaid = invoices.data.filter(({ status }: Invoice) => status === 'unpaid')
            setOutstandingInvoices(unpaid)
        }

        if (paymentMethodsSuccess && paymentMethods?.length > 0) {
            const method = paymentMethods.find(({ is_default: isDefault }) => isDefault)
            setDefaultPaymentMethod(method)
        }
    }, [invoicesSuccess, invoices])

    /*   JSX FUNCTIONS
     **********************************************************************************************************/
    const AccountPayment = () => (
        <AccountPaymentWrapper>
            <Payment.Container>
                <Payment.Title>Account Credit</Payment.Title>
                <div>{aud(data?.account_credit)}</div>
            </Payment.Container>
            <Payment.Container>
                <Payment.Title>Primary Payment Method</Payment.Title>
                <Method.Default.Container>
                    {
                        defaultPaymentMethod && <Method.Default.Type>
                            <svg {...images[defaultPaymentMethod.type].props} />
                        </Method.Default.Type>
                    }
                    <Method.Default.Identifier>
                        {defaultPaymentMethod ? `**** **** **** ${defaultPaymentMethod.identifier}` : 'Not Provided'}
                    </Method.Default.Identifier>
                </Method.Default.Container>
            </Payment.Container>
        </AccountPaymentWrapper>
    )

    const InvoicePayment = () => (
        <InvoicePaymentWrapper>
            <Button
                color={'primary'}
                disabled={invoicesLoading || (Array.isArray(outstandingInvoices) && outstandingInvoices.length <= 0)}
                onClick={() => dispatch(setInvoice(outstandingInvoices?.[0].id))}
            >
                Pay Invoice
            </Button>
            <Button color={'secondary'} onClick={() => navigate('/account')}>
                Account
            </Button>
        </InvoicePaymentWrapper>
    )

    function setInitialTab() {
        switch (location.pathname) {
            case '/billing/paymentmethods':
                return 'Payment Method'

            case '/billing/transactions':
                return 'Transactions'

            default:
                return 'Invoices'
        }
    }

    /**********************************************************************************************************
     *   RENDER
     **********************************************************************************************************/
    return (
        <FrameInset
            inset={'split'}
            location={{ pathname: '/billing' }}
            left={{
                render: (
                    <Overview>
                        <Bubble
                            title={'Invoices Due'}
                            desc={outstandingInvoices ? outstandingInvoices.length : 0}
                            icon={<DocumentTextIcon />}
                        />
                        <AccountPayment />
                        <InvoicePayment />
                    </Overview>
                )
            }}
            right={{
                render: (
                    <TabsWrapper initialTab={setInitialTab()} tabs={[
                        {
                            icon: ServerIcon,
                            label: 'Invoices',
                            render: <Invoices />,
                            location: () => navigate('/billing/invoices', { replace: true })
                        },
                        {
                            icon: DocumentReportIcon,
                            label: 'Transactions',
                            render: <Transactions />,
                            location: () => navigate('/billing/transactions', { replace: true })
                        },
                        {
                            icon: CreditCardIcon,
                            label: 'Payment Method',
                            render: <PaymentMethods />,
                            location: () => navigate('/billing/paymentmethods', { replace: true })
                        }
                    ]} />
                )
            }}
        />
    )
}
